import {
  Card,
  Tag,
  Row,
  Col,
  Progress,
  Typography,
  Collapse,
  Button,
  Dropdown,
  MenuProps,
} from "antd";
import PendingApplicationsBanner from "./PendingApplicationsBanner";
import { CampaignListItem } from "interfaces/campaign-list-item";
import { useLoaderData, useNavigate, useNavigation } from "react-router-dom";
import dayjs from "dayjs";
import "./Campaigns.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarCheck,
  faClock,
  faClockFour,
  faEllipsisVertical,
  faEye,
  faFileSignature,
  faLocationDot,
  faPaperPlane,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";
import MiniTable from "components/mini-table";
import { ReactComponent as PlayIcon } from "assets/images/play-icon.svg";
import { ReactComponent as HeartIcon } from "assets/images/heart-icon.svg";
import { ReactComponent as ReachIcon } from "assets/images/reach-icon.svg";
import { ReactComponent as FlashIcon } from "assets/images/flash-icon.svg";
import { ReactComponent as MessageIcon } from "assets/images/message-text-icon.svg";
import { ReactComponent as RepeatIcon } from "assets/images/repeat-icon.svg";
import { ReactComponent as InstagramIcon } from "assets/images/instagram-icon.svg";
import { ReactComponent as TikTokIcon } from "assets/images/tiktok-icon.svg";
import { ReactComponent as PurchaseIcon } from "assets/images/profile-icon.svg";
import OpenCollabs from "assets/images/create-collab-success.png";
import {
  formatNumberKMBT,
  ImgWithErrorHandling,
} from "pages/influencers/InfluencerCard";
import { openNewTabSafe } from "helpers/url-helpers";
import BoostProcurement from "./BoostProcurement";
import Loader from "components/loader/Loader";

interface ClicableLabelProps {
  className?: string;
  icon?: React.ReactNode;
  label?: string;
  value?: string;
  isClickable?: boolean;
  onClick?: () => void;
  extraInfo?: React.ReactNode;
}

const ClickableLabel: React.FC<ClicableLabelProps> = ({
  icon,
  label,
  value,
  isClickable,
  extraInfo,
  onClick,
}) => {
  return (
    <div
      className={`row ${isClickable ? "clickable" : ""}`}
      onClick={() => isClickable && onClick && onClick()}
    >
      {icon} <span style={{ fontWeight: 600 }}>{value}</span>
      <span className="decoration">{label}</span>
      {extraInfo}
    </div>
  );
};

enum Status {
  Active = "active",
  Pending = "pending",
  Completed = "completed",
  Cancelled = "cancelled",
}

// enum NewStatus {
//   Launched = "launched",
//   Pending = "pending",
//   Completed = "completed",
//   Cancelled = "cancelled",
// }

const statusLabels: Record<Status, string> = {
  [Status.Active]: "Active",
  [Status.Pending]: "Pending Launch",
  [Status.Completed]: "Completed",
  [Status.Cancelled]: "Cancelled",
};

const colorByStatus: Record<Status, string> = {
  [Status.Active]: "#52c41a",
  [Status.Pending]: "#faad14",
  [Status.Completed]: "#1890ff",
  [Status.Cancelled]: "#f5222d",
};

const Campaigns: React.FC = () => {
  const campaigns = useLoaderData() as CampaignListItem[];
  const navigate = useNavigate();
  const navigation = useNavigation();
  const loading = navigation.state === "loading";

  const showEmptyState = campaigns.length === 0;

  const onClickOpenPost = (url: string) => {
    if (!url) return;
    openNewTabSafe(url);
  };

  const onClickCreateCampaign = () => {
    navigate("/campaign/new");
  };

  const getMenuItems = (campaign: CampaignListItem): MenuProps["items"] => [
    {
      key: "1",
      label: "Edit Offer",
      onClick: () => {
        navigate(`/campaigns/${campaign._id}/edit-offer`);
      },
    },
    {
      type: "divider",
    },
    {
      key: "2",
      label: "Edit Instructions",
      onClick: () => {
        navigate(`/campaigns/${campaign._id}/edit-instructions`);
      },
    },
  ];

  if (showEmptyState) {
    return (
      <div className="campaigns">
        <Card>
          <Row justify="center">
            <Col md={12} xs={24} style={{ textAlign: "center" }}>
              <Row justify="center" style={{ marginBottom: 32 }}>
                <img src={OpenCollabs} alt="Collabs empty" />
              </Row>
              <Typography.Text className="text-regular">
                Welcome! Ready to launch your new campaign? Click below to get
                started.
              </Typography.Text>
              <Row justify="center" style={{ marginTop: 20 }}>
                <Button type="primary" onClick={onClickCreateCampaign}>
                  Create Campaign
                </Button>
              </Row>
            </Col>
          </Row>
        </Card>
      </div>
    );
  }

  return (
    <div
      className="campaigns"
      style={{ display: "flex", justifyContent: "center" }}
    >
      <Loader visible={loading} />
      <div style={{ maxWidth: 1400, width: "100%" }}>
        <PendingApplicationsBanner />
        {campaigns.map((campaign) => (
          <Card key={campaign._id || campaign.name}>
            <Row justify="space-between" align="middle">
              <Col>
                <div className="campaigns__header">
                  <span>{campaign.name}</span>
                  <Tag
                    color={
                      colorByStatus[campaign.status as Status] ?? "#d9d9d9"
                    }
                    className="campaigns__tag"
                  >
                    {statusLabels[campaign.status as Status]}
                  </Tag>
                </div>
                <div className="campaigns__dates">
                  {dayjs(campaign.startDate).format("MMMM D, YYYY")} -{" "}
                  {dayjs(campaign.endEnd).format("MMMM D, YYYY")}
                </div>
                <div className="campaigns__dates">
                  <FontAwesomeIcon icon={faLocationDot} /> Running across{" "}
                  {campaign?.collabs?.length} location
                </div>
              </Col>

              <Col
                style={{
                  textAlign: "right",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                {campaign.status !== Status.Pending &&
                  campaign.status !== Status.Completed && (
                    <div>
                      <div className="campaigns__delivery-label">
                        Predicted delivery
                      </div>
                      <div className="campaigns__delivery-value">
                        {campaign.predictedDelivery
                          ? dayjs(campaign.predictedDelivery).format(
                              "MMMM D, YYYY"
                            )
                          : "N/A"}
                      </div>
                    </div>
                  )}
                {campaign.status === Status.Pending && (
                  <div>
                    <div className="campaigns__delivery-label">
                      Expected Launch
                    </div>
                    <div className="campaigns__delivery-value">
                      {campaign.expectedLaunch
                        ? dayjs(campaign.expectedLaunch).format("MMMM D, YYYY")
                        : "N/A"}
                    </div>
                  </div>
                )}
                <Dropdown menu={{ items: getMenuItems(campaign) }}>
                  <Button>
                    <FontAwesomeIcon icon={faEllipsisVertical} />
                  </Button>
                </Dropdown>
              </Col>
            </Row>
            <div className="campaigns__progress">
              <Progress
                percent={campaign.progress}
                showInfo={false}
                strokeColor={
                  campaign.status === Status.Pending ? "#faad14" : "#52c41a"
                }
                trailColor="#F2F2F2"
                strokeWidth={12}
              />
              <Row className="campaigns__progress-labels">
                <Col>Launched</Col>
                {/* <Col>Procuring</Col>
              <Col>Preparing</Col> */}
                <Col>Completed</Col>
              </Row>
            </div>
            <Row gutter={32} className="campaigns__section">
              <Col flex={1}>
                <div
                  className={`campaigns__reachout ${
                    campaign.status === Status.Pending
                      ? "campaigns__disabled"
                      : ""
                  }`}
                >
                  <div className="title">Influencer Reach Out</div>
                  <div className="row">
                    <FontAwesomeIcon icon={faPaperPlane} />
                    <span style={{ fontWeight: 600 }}>
                      {campaign.invitesSentAmount.toLocaleString()}
                    </span>{" "}
                    invites sent
                  </div>

                  <div className="calendar row">
                    <FontAwesomeIcon icon={faClock} />{" "}
                    {dayjs(campaign.lastReachOutAt).fromNow()} last reach out
                  </div>
                  <div className="row">
                    <span style={{ height: 20 }}></span>
                  </div>
                </div>
              </Col>
              <Col flex={1}>
                <div
                  className={`campaigns__procuring ${
                    campaign.status === Status.Pending
                      ? "campaigns__disabled"
                      : ""
                  }`}
                  style={{
                    display: "flex",
                    gap: 16,
                    width: "100%",
                    justifyContent: "space-around",
                  }}
                >
                  <div>
                    <div className="title">
                      Procuring Creators{" "}
                      {campaign.lowConversion ? (
                        <Tag color="#faad14" className="campaigns__tag">
                          Low Conversion
                        </Tag>
                      ) : null}
                    </div>
                    {/* <div className="row">
                    <FontAwesomeIcon icon={faEye} />
                    <span style={{ fontWeight: 600 }}>
                      {campaign.viewedAmount.toLocaleString()}
                    </span>{" "}
                    Viewed
                  </div>
                  <div
                    className="row"
                    style={{ cursor: "pointer" }}
                    onClick={() => navigate("/influencers?status=pending")}
                  >
                    <FontAwesomeIcon icon={faFileSignature} />
                    <span style={{ fontWeight: 600 }}>
                      {campaign.appliedAmount.toLocaleString()}
                    </span>{" "}
                    Applied
                  </div>

                  <div
                    className="row"
                    style={{ cursor: "pointer" }}
                    onClick={() => navigate("/influencers?status=accepted")}
                  >
                    <FontAwesomeIcon icon={faCalendarCheck} />
                    <span style={{ fontWeight: 600 }}>
                      {campaign.bookedAmount.toLocaleString()}
                    </span>{" "}
                      
                  </div>
                    <div
                    className="row"
                    style={{ cursor: "pointer" }}
                    onClick={() => navigate("/influencers?status=expired")}
                  >
                    <FontAwesomeIcon icon={faCalendarCheck} />
                    <span style={{ fontWeight: 600 }}>
                      {campaign.expiredAmount || "-"}
                    </span>{" "}
                    Expired
                  </div> */}
                    <ClickableLabel
                      icon={<FontAwesomeIcon icon={faEye} />}
                      label="Viewed"
                      value={campaign.viewedAmount.toLocaleString()}
                    />
                    <ClickableLabel
                      icon={<FontAwesomeIcon icon={faFileSignature} />}
                      label="Applied"
                      value={campaign.appliedAmount.toLocaleString()}
                      onClick={() =>
                        navigate(
                          "/applications?status=pending&campaignId=" +
                            campaign._id
                        )
                      }
                      isClickable={campaign.appliedAmount > 0}
                      extraInfo={
                        campaign.lowConversion ? (
                          <div
                            style={{
                              background: "#FFEDE2",
                              color: "#eb580d",
                              padding: "2px 6px",
                              borderRadius: 4,
                              marginLeft: 8,
                            }}
                          >
                            0.5%
                          </div>
                        ) : null
                      }
                    />
                    <ClickableLabel
                      icon={<FontAwesomeIcon icon={faCalendarCheck} />}
                      label="Booked"
                      value={campaign.bookedAmount.toLocaleString()}
                      onClick={() => navigate("/applications?status=accepted")}
                      isClickable={campaign.bookedAmount > 0}
                    />

                    <ClickableLabel
                      icon={<FontAwesomeIcon icon={faClockFour} />}
                      label="Expired"
                      value={campaign.expiredAmount.toLocaleString()}
                      onClick={() => navigate("/applications?status=expired")}
                      isClickable={campaign.expiredAmount > 0}
                    />
                  </div>
                </div>
              </Col>
              <Col flex={1}>
                <div
                  className={`campaigns__content ${
                    campaign.status === Status.Pending
                      ? "campaigns__disabled"
                      : ""
                  }`}
                >
                  <MiniTable
                    rows={campaign.bookings}
                    emptyMessage="No Bookings Yet"
                  />
                </div>
              </Col>
              {/* <Col flex={1}>
              <div
                className={`campaigns__booking ${
                  campaign.status === Status.Pending
                    ? "campaigns__disabled"
                    : ""
                }`}
              >
                <div className="title">Accepted Booking</div>
                <div className="mini-table">
                  <div className="mini-table__header">
                    <div className="mini-table__col mini-table__col--photo"></div>
                    <div className="mini-table__col">Visit</div>
                    <div className="mini-table__col">Content due</div>
                  </div>
                  {[
                    {
                      photo: "https://randomuser.me/api/portraits/men/32.jpg",
                      visitDate: "2024-06-10",
                      contentDue: "2024-06-15",
                    },
                    {
                      photo: "https://randomuser.me/api/portraits/women/44.jpg",
                      visitDate: "2024-06-12",
                      contentDue: "2024-06-18",
                    },
                    {
                      photo: "https://randomuser.me/api/portraits/men/65.jpg",
                      visitDate: "2024-06-14",
                      contentDue: "2024-06-20",
                    },
                  ].map((row, idx) => (
                    <div className="mini-table__row" key={idx}>
                      <div className="mini-table__col mini-table__col--photo">
                        <img
                          src={row.photo}
                          alt="Influencer"
                          style={{
                            width: 28,
                            height: 28,
                            borderRadius: "50%",
                            objectFit: "cover",
                            border: "1px solid #eee",
                          }}
                        />
                      </div>
                      <div className="mini-table__col">
                        {dayjs(row.visitDate).format("MMM D, YYYY")}
                      </div>
                      <div className="mini-table__col">
                        {dayjs(row.contentDue).format("MMM D, YYYY")}
                      </div>
                    </div>
                  ))}
                  <div
                    className="mini-table__footer"
                    style={{ textAlign: "center" }}
                  >
                    <span style={{ color: "#888" }}>
                      {" "}
                      <FontAwesomeIcon icon={faChevronDown} /> View more (21)
                    </span>
                  </div>
                </div>
              </div>
            </Col> */}
              {/* <Col flex={1}>
              <div
                className={`campaigns__content ${
                  campaign.status === Status.Pending
                    ? "campaigns__disabled"
                    : ""
                }`}
              >
                <div className="title">Content Submitted</div>
                <div className="row">
                  <CheckCircleFilled />
                  {campaign.contentSubmitted}/{campaign.contentSubmittedPending}
                </div>
                <div className="posts-live">Posts Live</div>
              </div>
            </Col> */}
            </Row>

            {campaign.lowConversion && <BoostProcurement campaign={campaign} />}
            <Collapse
              defaultActiveKey={[]}
              className="collapse"
              style={{ margin: "32px 0 0 0" }}
            >
              <Collapse.Panel
                header={
                  <span style={{ fontWeight: 600, fontSize: 16 }}>
                    Overall Content Performance
                  </span>
                }
                key="1"
              >
                <Row
                  gutter={[12, 12]}
                  className={
                    campaign.status === Status.Pending
                      ? "campaigns__disabled"
                      : ""
                  }
                >
                  <Col xs={4}>
                    <Card className="card-performance">
                      <ReachIcon className="icon" />
                      <Typography.Title level={5} style={{ margin: 0 }}>
                        {formatNumberKMBT(campaign?.bookingsStats?.totalReach)}
                      </Typography.Title>
                      <span>Reach</span>
                    </Card>
                  </Col>
                  <Col xs={4}>
                    <Card className="card-performance">
                      <PlayIcon className="icon" />
                      <Typography.Title level={5} style={{ margin: 0 }}>
                        {formatNumberKMBT(campaign?.bookingsStats?.totalViews)}
                      </Typography.Title>
                      <span>Plays</span>
                    </Card>
                  </Col>
                  <Col xs={4}>
                    <Card className="card-performance">
                      <HeartIcon className="icon" />
                      <Typography.Title level={5} style={{ margin: 0 }}>
                        {formatNumberKMBT(campaign?.bookingsStats?.totalLikes)}
                      </Typography.Title>
                      <span>Likes</span>
                    </Card>
                  </Col>
                  <Col xs={4}>
                    <Card className="card-performance">
                      <MessageIcon className="icon" />
                      <Typography.Title level={5} style={{ margin: 0 }}>
                        {formatNumberKMBT(
                          campaign?.bookingsStats?.totalComments
                        )}
                      </Typography.Title>
                      <span>Comments</span>
                    </Card>
                  </Col>
                  <Col xs={4}>
                    <Card className="card-performance">
                      <RepeatIcon className="icon" />
                      <Typography.Title level={5} style={{ margin: 0 }}>
                        {formatNumberKMBT(
                          campaign?.bookingsStats?.totalReshares
                        )}
                      </Typography.Title>
                      <span>Shares</span>
                    </Card>
                  </Col>
                  <Col xs={4}>
                    <Card className="card-performance">
                      <FlashIcon className="icon" />
                      <Typography.Title level={5} style={{ margin: 0 }}>
                        {campaign?.bookingsStats?.totalEngagement}%
                      </Typography.Title>
                      <span>Engagement</span>
                    </Card>
                  </Col>
                </Row>
              </Collapse.Panel>
            </Collapse>
            <Collapse defaultActiveKey={[]} className="collapse">
              <Collapse.Panel
                header={
                  <span style={{ fontWeight: 600, fontSize: 16 }}>Posts</span>
                }
                key="posts"
              >
                <Row gutter={[12, 12]}>
                  <Col xs={24} className="list-post">
                    {campaign.bookingsPosts &&
                    campaign.bookingsPosts.length > 0 ? (
                      campaign.bookingsPosts.map((post: any, index) => (
                        <div
                          className="post"
                          key={`POST_${index}_${post.engagementRate}`}
                          onClick={() => onClickOpenPost(post.socialMediaUrl)}
                        >
                          <ImgWithErrorHandling src={post.coverImg} alt="" />
                          <span className="social-icon">
                            {post.socialMediaName?.toLowerCase() ===
                            "tiktok" ? (
                              <TikTokIcon className="stroke" />
                            ) : (
                              <InstagramIcon className="fill" />
                            )}
                          </span>
                          <div className="info">
                            <span className="text">
                              <FlashIcon /> {post.engagementRate || "-"}% Eng
                            </span>
                            <span className="text">
                              <PlayIcon /> {formatNumberKMBT(post.reach) || "-"}{" "}
                              views
                            </span>
                            {!!post.dinner && (
                              <span className="text">
                                <PurchaseIcon />
                                {post.dinner || "-"} Purchase Intent
                              </span>
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="no-posts">
                        <FontAwesomeIcon icon={faTriangleExclamation} />
                        <Typography.Title
                          level={5}
                          style={{ color: "#888", margin: 0 }}
                        >
                          No content available
                        </Typography.Title>
                        <Typography.Text style={{ color: "#888", margin: 0 }}>
                          Complete procurement to see posts
                        </Typography.Text>
                      </div>
                    )}
                  </Col>
                </Row>
              </Collapse.Panel>
            </Collapse>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default Campaigns;
